import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * Format a date for display with exact date and time.
 */
function formatVersionDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get a display label for a version.
 */
function getVersionLabel(version) {
  if (version.label) {
    return `"${version.label}"`;
  }
  return formatVersionDate(version.created_at);
}

/**
 * VersionSelector - Collapsible panel for selecting chunk_data versions.
 */
export default function VersionSelector({
  versions,
  selectedVersionId,
  isLoading,
  onSelectVersion,
  onClearSelection,
}) {
  const [expanded, setExpanded] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);

  const isVersionLoaded = Boolean(selectedVersionId);

  const handleToggleExpand = () => {
    if (!isVersionLoaded) {
      setExpanded(!expanded);
      setPendingSelection(null);
    }
  };

  const handleSelectPending = (versionId) => {
    setPendingSelection(versionId);
  };

  const handleLoadVersion = () => {
    if (pendingSelection) {
      onSelectVersion(pendingSelection);
      setExpanded(false);
      setPendingSelection(null);
    }
  };

  const handleCancel = () => {
    setExpanded(false);
    setPendingSelection(null);
  };

  const handleClearVersion = () => {
    onClearSelection();
  };

  // Version loaded state
  if (isVersionLoaded) {
    const loadedVersion = versions.find((v) => v.id === selectedVersionId);
    return (
      <Paper
        elevation={1}
        sx={{
          mx: 3,
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderLeft: 3,
          borderColor: 'success.main',
        }}
      >
        <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            Using saved version
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {loadedVersion ? getVersionLabel(loadedVersion) : 'Unknown version'}
            {loadedVersion && ` · ${loadedVersion.chunk_count} chunks`}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={handleClearVersion}
        >
          Use Fresh Instead
        </Button>
      </Paper>
    );
  }

  // Collapsed state - versions available
  if (!expanded) {
    return (
      <Paper
        elevation={1}
        sx={{
          mx: 3,
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={handleToggleExpand}
      >
        <HistoryIcon sx={{ color: 'text.secondary' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            Saved versions available
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {versions.length} version{versions.length !== 1 ? 's' : ''} found for this EAN
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          endIcon={<ExpandMoreIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleToggleExpand();
          }}
        >
          Browse
        </Button>
      </Paper>
    );
  }

  // Expanded state - selecting a version
  return (
    <Paper
      elevation={1}
      sx={{
        mx: 3,
        mt: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <HistoryIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
          Select a version to load
        </Typography>
        <IconButton size="small" onClick={handleToggleExpand}>
          <ExpandLessIcon />
        </IconButton>
      </Box>

      {/* Version List */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <RadioGroup
              value={pendingSelection || ''}
              onChange={(e) => handleSelectPending(parseInt(e.target.value, 10))}
            >
              {versions.map((version) => (
                <FormControlLabel
                  key={version.id}
                  value={version.id}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body2">
                        {getVersionLabel(version)}
                      </Typography>
                      <Chip
                        label={`${version.chunk_count} chunks`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                      {version.source === 'manual' && (
                        <Chip
                          label="manual"
                          size="small"
                          sx={{ height: 22 }}
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    mx: 0,
                    mb: 0.5,
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                />
              ))}
            </RadioGroup>
          )}

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!pendingSelection || isLoading}
              onClick={handleLoadVersion}
            >
              Load Selected
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}
